import { useEffect, useState, useRef, useCallback, type FormEvent } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import { isTenantUser } from "../lib/authRole";
import {
  getLeadDetail, updateLeadStatus, getTenant, getLeadVersion, saveLeadVersion,
  createLeadNote,
  type LeadDetail, ApiError, type LeadStatus, type TenantConfig,
} from "../lib/api";
import {
  buildEmbedUrl, getEmbedOrigin, isEmbedReady, isDesignLoaded, isDesignSnapshot,
} from "../lib/embedMessaging";
import { getVersionMetadata, getCurrentVersionId } from "../lib/designVersions";
import { ExportsCard } from "../components/ExportsCard";
import { VersionHistoryPanel } from "../components/VersionHistoryPanel";
import { ActivityFeedPanel } from "../components/ActivityFeedPanel";
import { StatusMessage } from "../components/StatusMessage";
import { friendlyErrorMessage } from "../components/friendlyErrorMessage";
import { STAGE_COLORS, STAGE_LABELS, stageOf } from "../lib/leads";
import ui from "../styles/ui.module.css";
import { estimateRangeFromDesign } from "../lib/estimate";
import styles from "./LeadDetailPage.module.css";

// Round 4 — live-edit capstone. How long to wait for the embed's
// design-snapshot reply to a request-snapshot before giving up. Generous
// — this is same-page postMessage, not a network call — but the embed
// could in principle be wedged (e.g. a WebGL context-loss the user
// hasn't dismissed yet), and Save must never hang forever.
const SNAPSHOT_TIMEOUT_MS = 10_000;

export function LeadDetailPage() {
  const { idempotencyKey } = useParams();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMutatingStatus, setIsMutatingStatus] = useState(false);
  const [embedReady, setEmbedReady] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // AUD-021 — focus management entering/exiting edit mode, the biggest
  // mode/context change this page has (the closest thing to a modal it
  // offers). cancelEditButtonRef: focused on entry. editDesignButtonRef:
  // refocused on exit, mirroring a modal's open/close focus contract.
  const cancelEditButtonRef = useRef<HTMLButtonElement>(null);
  const editDesignButtonRef = useRef<HTMLButtonElement>(null);

  // Version history (Round 2 f1-version-history). viewingVersionId === null
  // means the preview below is showing the lead's CURRENT design;
  // otherwise it's showing viewingDesignState, fetched via getLeadVersion().
  const [viewingVersionId, setViewingVersionId] = useState<string | null>(null);
  const [viewingDesignState, setViewingDesignState] = useState<unknown>(null);
  const [pendingViewVersionId, setPendingViewVersionId] = useState<string | null>(null);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(null);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  // Live in-iframe editing (Round 4 — live-edit capstone). isEditMode swaps
  // the SAME iframe's src to the embed's editable surface (?edit=1);
  // toggling it resets embedReady since that src change forces a fresh
  // mount. designLoadedInEditMode only ever means "the CURRENT edit
  // session's design has been applied inside the embed" (see
  // embedMessaging.ts's design-loaded doc) — Save is gated on this, not on
  // embedReady, so a save can never race the initial design load.
  const [isEditMode, setIsEditMode] = useState(false);
  const [designLoadedInEditMode, setDesignLoadedInEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  // A stale-version (409) conflict is tracked separately from saveError —
  // it has its own affordance (Reload latest design) rather than just an
  // error string, so the two must never collapse into one "error" flag.
  const [saveConflict, setSaveConflict] = useState(false);
  // Guards "send load-design exactly once per edit session" — see the
  // effect below. A ref (not state) since it's an internal bookkeeping
  // flag, not something that should trigger a render.
  const sentInitialEditDesignRef = useRef(false);
  // The one in-flight request-snapshot's requestId + promise settlers, or
  // null when no snapshot request is outstanding. A single slot (not a
  // Map) is deliberate — the Save button is disabled while isSaving, so
  // at most one request-snapshot round trip is ever in flight.
  const pendingSnapshotRef = useRef<{
    requestId: string;
    resolve: (payload: unknown) => void;
    reject: (err: Error) => void;
  } | null>(null);
  // Mirrors isEditMode for handleViewVersion's async guard (below) — a
  // plain closure over isEditMode would see whatever it was WHEN
  // handleViewVersion was called, not whatever it becomes while that
  // fetch is still in flight.
  const isEditModeRef = useRef(false);
  useEffect(() => { isEditModeRef.current = isEditMode; }, [isEditMode]);

  // AUD-021 — move focus to the Cancel/Save controls on entering edit mode,
  // and back to the "Edit design" button on exit (the same pattern a
  // modal's open/close would use). `wasEditModeRef` (not `lead`/anything
  // else) is what distinguishes "just exited" from "mounted already not
  // editing" — the latter must NOT steal focus from RouteFocusManager's
  // own just-navigated-here heading focus.
  const wasEditModeRef = useRef(false);
  useEffect(() => {
    if (isEditMode) {
      cancelEditButtonRef.current?.focus();
    } else if (wasEditModeRef.current) {
      editDesignButtonRef.current?.focus();
    }
    wasEditModeRef.current = isEditMode;
  }, [isEditMode]);

  // NOTE — "Save as template" used to live here (Round 4 D1) and is gone as
  // of round8/template-authoring. Template authoring moved out of the lead
  // entirely: it is now its own destination (pages/TemplateEditorPage.tsx,
  // reached from Settings -> Templates) where a builder draws on a real
  // designer canvas rather than capturing whichever lead they happened to
  // be looking at. Nothing about a lead's own design/version handling
  // changed with that removal.

  // Note composer (round7/lead-activity-portal). noteAdded mirrors
  // SettingsPage's own `saved` flag/timeout — a transient "Note added."
  // confirmation, not a persistent one.
  const [noteText, setNoteText] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteAdded, setNoteAdded] = useState(false);

  // When the route switches to a different lead, reset load state DURING RENDER
  // (React's "adjusting state on a change" pattern) rather than with a
  // synchronous setState inside the fetch effect below — the latter trips
  // react-hooks/set-state-in-effect and causes a cascading render.
  const [loadedKey, setLoadedKey] = useState(idempotencyKey);
  if (loadedKey !== idempotencyKey) {
    setLoadedKey(idempotencyKey);
    setLead(null);
    setIsLoading(true);
    setError(null);
    setViewingVersionId(null);
    setViewingDesignState(null);
    setPendingViewVersionId(null);
    setRestoringVersionId(null);
    setVersionsError(null);
    setIsEditMode(false);
    setEmbedReady(false);
    setDesignLoadedInEditMode(false);
    setSaveError(null);
    setSaveConflict(false);
    setSaveLabel("");
    setNoteText("");
    setIsSubmittingNote(false);
    setNoteError(null);
    setNoteAdded(false);
  }

  // Fetch the lead + tenant on mount (parallel). Auto-acknowledge happens
  // server-side when the lead detail is fetched. Tenant is needed so the PDF
  // export can apply branding.
  useEffect(() => {
    if (!user || !idempotencyKey) return;

    let cancelled = false;

    Promise.all([
      getLeadDetail(user.idToken, idempotencyKey),
      getTenant(user.idToken),
    ])
      .then(([leadResult, tenantResult]) => {
        if (cancelled) return;
        setLead(leadResult);
        setTenant(tenantResult);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setError("Lead not found.");
          return;
        }
        setError(friendlyErrorMessage(err, "loading this lead"));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [user, idempotencyKey, signOut]);

  // AUD-006 — a manual, in-place retry for the load-failure branch below
  // (previously the only recovery was "Back to leads," i.e. leaving the
  // page). Deliberately a separate implementation from the mount effect
  // above rather than a shared callback, mirroring LeadsPage's own
  // loadLeads/mount-effect split — this one is user-triggered (a button
  // click), so it doesn't need the effect's unmount-race `cancelled` guard.
  const handleRetryLoad = useCallback(() => {
    if (!user || !idempotencyKey) return;
    setError(null);
    setIsLoading(true);
    Promise.all([
      getLeadDetail(user.idToken, idempotencyKey),
      getTenant(user.idToken),
    ])
      .then(([leadResult, tenantResult]) => {
        setLead(leadResult);
        setTenant(tenantResult);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setError("Lead not found.");
          return;
        }
        setError(friendlyErrorMessage(err, "loading this lead"));
      })
      .finally(() => setIsLoading(false));
  }, [user, idempotencyKey, signOut]);

  // Listen for messages from the embed (embed -> portal, one shared
  // listener for all three message types it can send). embed-ready and
  // design-loaded just flip a flag; design-snapshot resolves whichever
  // request-snapshot call is currently awaiting one (see requestSnapshot
  // below) — at most one is ever outstanding, so matching by requestId
  // (rather than trusting arrival order) is belt-and-suspenders, not load
  // -bearing, but costs nothing and guards against a stray/duplicate reply.
  useEffect(() => {
    if (!lead) return;

    const allowedOrigin = getEmbedOrigin();

    const handler = (event: MessageEvent) => {
      if (allowedOrigin && event.origin !== allowedOrigin) return;
      if (isEmbedReady(event.data)) {
        setEmbedReady(true);
        return;
      }
      if (isDesignLoaded(event.data)) {
        setDesignLoadedInEditMode(true);
        return;
      }
      if (isDesignSnapshot(event.data)) {
        const pending = pendingSnapshotRef.current;
        if (pending && event.data.requestId === pending.requestId) {
          pendingSnapshotRef.current = null;
          pending.resolve(event.data.payload);
        }
        return;
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [lead]);

  // Send whichever designState is currently selected for preview — the
  // lead's current design, or an old version fetched via handleViewVersion
  // below — through the SAME one-way, read-only `load-design` postMessage
  // (portal -> embed) used to preview the current design. Fires once the
  // embed announces readiness, and again whenever the selection changes
  // (switching to an old version, or back to "current") — the embed only
  // announces "embed-ready" once on load, so re-sending on selection change
  // (rather than only in the ready handler above) is what lets a version
  // click update an already-loaded iframe. Nothing here is new/bidirectional:
  // still the one existing message type, still portal -> embed only.
  //
  // Skipped entirely in edit mode (`isEditMode` guard) — that surface has
  // its own one-shot load effect below (sending load-design more than once
  // into an in-progress edit session would silently discard unsaved work).
  useEffect(() => {
    if (!embedReady || !lead || isEditMode) return;
    const payload = viewingVersionId !== null ? viewingDesignState : lead.designState;
    if (payload === null || payload === undefined) return; // old version still loading
    const allowedOrigin = getEmbedOrigin();
    iframeRef.current?.contentWindow?.postMessage(
      { type: "load-design", payload },
      allowedOrigin || "*",
    );
  }, [embedReady, lead, viewingVersionId, viewingDesignState, isEditMode]);

  // Live-edit mode (Round 4 — live-edit capstone): send the lead's CURRENT
  // design into the editable embed exactly ONCE per edit session, as soon
  // as it announces embed-ready. sentInitialEditDesignRef (not `lead` or
  // any other reactive value) is what makes this "once" — a later refresh
  // of `lead` (e.g. right after a successful Save) must NOT re-trigger a
  // load-design, or it would stomp on whatever the builder is editing next.
  // Reset the ref when leaving edit mode so the NEXT time it's entered, the
  // send happens again for that new session.
  useEffect(() => {
    if (!isEditMode) {
      sentInitialEditDesignRef.current = false;
      return;
    }
    if (!embedReady || !lead) return;
    if (sentInitialEditDesignRef.current) return;
    sentInitialEditDesignRef.current = true;
    const allowedOrigin = getEmbedOrigin();
    iframeRef.current?.contentWindow?.postMessage(
      { type: "load-design", payload: lead.designState },
      allowedOrigin || "*",
    );
  }, [isEditMode, embedReady, lead]);

  const handleStatusToggle = useCallback(async () => {
    if (!user || !lead || isMutatingStatus) return;
    setIsMutatingStatus(true);
    const newStatus: LeadStatus =
      lead.submissionStatus === "closed" ? "new" : "closed";
    try {
      const updated = await updateLeadStatus(
        user.idToken, lead.idempotencyKey, newStatus,
      );
      setLead(updated);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      setError(friendlyErrorMessage(err, "updating this lead's status"));
    } finally {
      setIsMutatingStatus(false);
    }
  }, [user, lead, isMutatingStatus, signOut]);

  // View an old version: fetch its full designState and hand it to the
  // preview effect above via viewingDesignState/viewingVersionId.
  //
  // Round 4 — live-edit capstone: the VersionHistoryPanel button that
  // triggers this is disabled while isEditMode, but that only blocks
  // STARTING a new fetch — it can't cancel one already in flight. If the
  // user clicks View, then clicks Edit design before this fetch resolves,
  // isEditModeRef.current will be true by the time the `await` returns; the
  // guard below drops the result instead of flipping viewingVersionId to a
  // stale version's id while the page is now mid-edit.
  const handleViewVersion = useCallback(async (versionId: string) => {
    if (!user || !lead) return;
    setVersionsError(null);
    setPendingViewVersionId(versionId);
    try {
      const entry = await getLeadVersion(user.idToken, lead.idempotencyKey, versionId);
      if (isEditModeRef.current) return;
      setViewingDesignState(entry.designState);
      setViewingVersionId(versionId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      setVersionsError(err instanceof Error ? err.message : "Failed to load version");
    } finally {
      setPendingViewVersionId(null);
    }
  }, [user, lead, signOut]);

  const handleViewCurrentDesign = useCallback(() => {
    setViewingVersionId(null);
    setViewingDesignState(null);
  }, []);

  // Restore an old version: PUT .../versions with that version's own
  // designState, baseVersionId'd against the lead's CURRENT
  // currentVersionId (optimistic concurrency — see saveLeadVersion's doc
  // comment). On success, refetch the lead so the version list + quote
  // fields reflect the new current version. On 409 (the lead moved since
  // this page loaded), surface the copy verbatim — no silent retry, no
  // client-side merge attempt, matching SettingsPage's save-error handling
  // (setSaveError + a plain message, nothing automatic).
  const handleRestoreVersion = useCallback(async (versionId: string) => {
    if (!user || !lead) return;
    setVersionsError(null);
    setRestoringVersionId(versionId);
    try {
      const entry = await getLeadVersion(user.idToken, lead.idempotencyKey, versionId);
      const baseVersionId = getCurrentVersionId(lead.designState);
      if (!baseVersionId) {
        throw new Error("This lead has no version history to restore against yet.");
      }
      await saveLeadVersion(user.idToken, lead.idempotencyKey, {
        baseVersionId,
        designState: entry.designState,
      });
      const refreshed = await getLeadDetail(user.idToken, lead.idempotencyKey);
      setLead(refreshed);
      setViewingVersionId(null);
      setViewingDesignState(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        setVersionsError("This lead changed since you opened it — reload to continue.");
        return;
      }
      setVersionsError(err instanceof Error ? err.message : "Failed to restore version");
    } finally {
      setRestoringVersionId(null);
    }
  }, [user, lead, signOut]);

  // Round 4 — live-edit capstone. Enter the embed's editable surface: only
  // meaningful when viewing the CURRENT design (see the "Edit design"
  // button's own disabled condition below, gated on viewingVersionId,
  // restoringVersionId, AND pendingViewVersionId) — switching the SAME
  // iframe's src away from ?edit=1 while previewing an old version isn't a
  // supported combination, starting an edit session mid-restore would load
  // the design as of BEFORE the restore lands (silently orphaning it once
  // the restore's own lead-refresh completes), and starting one mid-"View"
  // fetch would let that fetch's resolution flip viewingVersionId to a
  // stale version's id while already editing. Explicitly clearing
  // viewingVersionId/viewingDesignState here too (not just relying on the
  // button being disabled) is belt-and-suspenders against exactly that
  // last race. embedReady/designLoadedInEditMode reset because the src
  // change forces the iframe to remount and re-announce readiness from
  // scratch.
  const handleEnterEditMode = useCallback(() => {
    setIsEditMode(true);
    setEmbedReady(false);
    setDesignLoadedInEditMode(false);
    setSaveError(null);
    setSaveConflict(false);
    setSaveLabel("");
    setViewingVersionId(null);
    setViewingDesignState(null);
  }, []);

  // Leave edit mode WITHOUT saving — the iframe's src reverts to the
  // read-only viewer (a fresh mount), discarding whatever the builder
  // hadn't saved. There is no "are you sure?" confirmation (see this
  // round's design note: DockDesigner exposes no dirty/undo-count signal
  // this page could check) — Cancel is a plain, unconfirmed discard, same
  // posture as e.g. closing a browser tab on an unsaved form.
  const handleExitEditMode = useCallback(() => {
    setIsEditMode(false);
    setEmbedReady(false);
    setDesignLoadedInEditMode(false);
    setSaveError(null);
    setSaveConflict(false);
  }, []);

  // Ask the currently-mounted edit iframe for its live design state and
  // resolve with the payload once design-snapshot arrives (matched by
  // requestId — see the shared message listener above). Rejects on
  // timeout or if the iframe isn't available. At most one call is ever
  // in flight (the Save button disables itself while isSaving).
  const requestSnapshotFromEmbed = useCallback((): Promise<unknown> => {
    return new Promise((resolve, reject) => {
      const win = iframeRef.current?.contentWindow;
      if (!win) {
        reject(new Error("The design editor isn't available right now."));
        return;
      }
      const requestId = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `snap-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const timeoutId = window.setTimeout(() => {
        if (pendingSnapshotRef.current?.requestId === requestId) {
          pendingSnapshotRef.current = null;
          reject(new Error("Timed out waiting for the design editor to respond."));
        }
      }, SNAPSHOT_TIMEOUT_MS);

      pendingSnapshotRef.current = {
        requestId,
        resolve: (payload) => { window.clearTimeout(timeoutId); resolve(payload); },
        reject: (err) => { window.clearTimeout(timeoutId); reject(err); },
      };

      const allowedOrigin = getEmbedOrigin();
      win.postMessage({ type: "request-snapshot", requestId }, allowedOrigin || "*");
    });
  }, []);

  // Save the live-edited design as a new version. Pulls the CURRENT state
  // out of the embed (not whatever was last loaded), then PUTs it with the
  // lead's current currentVersionId as baseVersionId — same optimistic-
  // concurrency contract handleRestoreVersion above already uses. On
  // success, refetches the lead (refreshes version history + quote
  // fields) but stays in edit mode so the builder can keep iterating.
  // On 409, sets saveConflict (its own affordance, below) rather than
  // silently retrying or merging.
  const handleSaveEdit = useCallback(async () => {
    if (!user || !lead || isSaving) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveConflict(false);
    try {
      const snapshot = await requestSnapshotFromEmbed();
      const baseVersionId = getCurrentVersionId(lead.designState);
      if (!baseVersionId) {
        throw new Error("This lead has no version history to save against yet.");
      }
      const trimmedLabel = saveLabel.trim();
      await saveLeadVersion(user.idToken, lead.idempotencyKey, {
        baseVersionId,
        designState: snapshot,
        ...(trimmedLabel ? { label: trimmedLabel } : {}),
      });
      const refreshed = await getLeadDetail(user.idToken, lead.idempotencyKey);
      setLead(refreshed);
      setSaveLabel("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        setSaveConflict(true);
        return;
      }
      setSaveError(err instanceof Error ? err.message : "Failed to save design");
    } finally {
      setIsSaving(false);
    }
  }, [user, lead, isSaving, saveLabel, requestSnapshotFromEmbed, signOut]);

  // The explicit, user-initiated recovery from a 409: pull the lead's true
  // current design and push it into the SAME open edit iframe, staying in
  // edit mode so the builder can pick up editing from the correct base
  // instead of losing their place entirely. This DOES discard whatever the
  // builder had unsaved in the iframe — that's the nature of "someone else's
  // save won" — but it is never automatic; the builder must click the
  // button. designLoadedInEditMode resets so Save re-arms only once the
  // embed confirms it actually applied this fresh design.
  const handleReloadLatestAfterConflict = useCallback(async () => {
    if (!user || !lead) return;
    setSaveConflict(false);
    setSaveError(null);
    try {
      const refreshed = await getLeadDetail(user.idToken, lead.idempotencyKey);
      setLead(refreshed);
      setDesignLoadedInEditMode(false);
      const allowedOrigin = getEmbedOrigin();
      iframeRef.current?.contentWindow?.postMessage(
        { type: "load-design", payload: refreshed.designState },
        allowedOrigin || "*",
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      setSaveError(err instanceof Error ? err.message : "Failed to reload the latest design");
    }
  }, [user, lead, signOut]);

  // Add a note (round7/lead-activity-portal — POST .../notes; either owner or
  // rep may call this, no client-side role gate needed). On success, refetch
  // the lead rather than locally splicing the new note into lead.notes/
  // activityFeed — same "mutate then refetch" posture handleSaveEdit/
  // handleRestoreVersion above already use for their own mutations, so this
  // doesn't need its own client-side copy of composeActivityFeed's sort/
  // interleave rules. No optimistic append, so there's nothing to roll back
  // on failure — the composer's text is only cleared once the POST (and the
  // refetch) actually succeed.
  //
  // Deliberately no "text is empty" early return here beyond the Add
  // button's own disabled state below (UX-only prevention of a pointless
  // click) — the server is the one source of truth for what counts as a
  // valid note (validateNoteText's MISSING_NOTE_TEXT/NOTE_TEXT_TOO_LONG),
  // and noteErrorMessage below surfaces its real message rather than this
  // component inventing a duplicate client-side check/copy for the same
  // rule (see that function's own doc comment).
  const handleSubmitNote = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!user || !lead || isSubmittingNote) return;
    const text = noteText.trim();
    setIsSubmittingNote(true);
    setNoteError(null);
    setNoteAdded(false);
    try {
      await createLeadNote(user.idToken, lead.idempotencyKey, text);
      const refreshed = await getLeadDetail(user.idToken, lead.idempotencyKey);
      setLead(refreshed);
      setNoteText("");
      setNoteAdded(true);
      setTimeout(() => setNoteAdded(false), 3000);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      setNoteError(noteErrorMessage(err));
    } finally {
      setIsSubmittingNote(false);
    }
  }, [user, lead, isSubmittingNote, noteText, signOut]);

  // round7/org-admin-panel-ui (P4.6-C): `!isTenantUser` narrows `user` to a
  // TenantUser for the rest of this render, which is what lets the embed URL
  // below read `user.tenantId` at all. An org admin has no tenantId and never
  // reaches this page (ProtectedRoute redirects them to /organization) — this
  // is the type system making that routing invariant explicit rather than a
  // new runtime branch.
  if (!user || !isTenantUser(user)) return null;

  if (isLoading) {
    return (
      <main id="main-content" className={ui.main}>
        <Link to="/leads" className={ui.backLink}>← Back to leads</Link>
        <h1>Lead</h1>
        <p className={ui.loading}>Loading lead…</p>
      </main>
    );
  }

  if (error && !lead) {
    return (
      /* AUD-007 / AUD-016: a real <main> with a real <h1> on this branch too. */
      <main id="main-content" className={ui.main}>
        <div className={ui.errorBox}>
          <h1>Unable to load lead</h1>
          <StatusMessage variant="error" message={error} className={ui.error} />
          <div className={ui.actions}>
            {/* AUD-006: an in-place Retry, not just an exit. */}
            <button type="button" onClick={handleRetryLoad} className={ui.button}>
              Retry
            </button>
            <button type="button" onClick={() => navigate("/leads")} className={ui.buttonSecondary}>
              Back to leads
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (!lead) return null;

  const isClosed = lead.submissionStatus === "closed";
  // `author: true` whenever this mount is editable (round8). It is NOT about
  // authoring templates here — the flag's real job is suppressing the embed's
  // HOMEOWNER chrome, and on the editable surface that chrome is actively
  // wrong for a builder:
  //
  // The embed gates its "Request Quote" lead-capture CTA (and its contact
  // modal) only on `readOnly`, which must be false for editing to work at
  // all. So before this flag, a builder editing a customer's design saw a
  // live CTA wired to a real POST /api/submit — clicking through it filed a
  // SPURIOUS LEAD into their own tenant, polluting their pipeline and
  // consuming a month's submit quota (basic tier caps at 50/month).
  // Confirmed against the live DEV embed, both ways, before this fix.
  //
  // It also suppresses the homeowner first-run onboarding modal ("Welcome…
  // Pick a starting point"), the guided tour, and the autosave restore
  // banner — all likewise addressed to a homeowner, not to a builder
  // editing someone else's saved design. The autosave DRAFT still persists
  // under its own scoped key, so nothing a builder draws is lost to the
  // embed's own storage; only the offer-to-restore banner is hidden. See
  // DockPro's DockDesigner `suppressHomeownerUI` JSDoc for the full list.
  //
  // Passed only alongside `edit` (buildEmbedUrl won't emit it otherwise —
  // the embed ignores `author` unless `preview=1&edit=1` already hold), so
  // the read-only viewer's URL is byte-identical to before.
  const embedUrl = buildEmbedUrl(user.tenantId, { edit: isEditMode, author: isEditMode });
  const versions = getVersionMetadata(lead.designState);
  const currentVersionId = getCurrentVersionId(lead.designState);
  const canSaveEdit = isEditMode && designLoadedInEditMode && !isSaving;
  // Round 1 tier model: costOutputs is "don't even generate/show a cost
  // figure" — see api.ts's TenantConfig.entitlements doc comment. Now
  // returned by GET /api/portal/tenant (Round 3, DockPro-Backend's
  // buildPortalTenantResponse) for every tenant; still guarded as
  // "not known to be restricted" unless explicitly `false` (only an
  // explicit `false` hides the quote comparison below, never re-derived
  // from a tier name per the Round 1 rule) so an older/stub tenant response
  // that predates this field can't wrongly hide a builder's numbers.
  const costOutputsEnabled = tenant?.entitlements?.costOutputs !== false;
  const viewingVersion = viewingVersionId
    ? versions.find(v => v.versionId === viewingVersionId)
    : undefined;
  const stage = stageOf(lead);

  return (
    <main id="main-content" className={`${ui.main} ${styles.main}`}>
      {/* Wayfinding row: where you are, what state this lead is in, and the
          one page-level action. The design itself comes right after the
          name — it is the artifact this page is about (round9/design-pass;
          it used to render 7th of 7, below an activity feed that grows
          without bound). */}
      <div className={styles.topRow}>
        <div className={styles.topRowLeft}>
          <Link to="/leads" className={ui.backLink}>← Back to leads</Link>
          <span className={`${ui.badge} ${statusTone(lead.submissionStatus)}`}>
            {lead.submissionStatus}
          </span>
          <span className="stage-badge" style={{ background: STAGE_COLORS[stage] }}>
            {STAGE_LABELS[stage]}
          </span>
        </div>
        <button
          type="button"
          className={ui.buttonSecondary}
          onClick={handleStatusToggle}
          disabled={isMutatingStatus}
        >
          {isMutatingStatus
            ? "Saving…"
            : isClosed ? "Reopen" : "Mark as closed"}
        </button>
      </div>

      {error && (
        <StatusMessage variant="error" message={error} className={ui.error} />
      )}

      <div className={styles.titleBlock}>
        <h1 className={styles.leadName}>{lead.homeownerName}</h1>
        <dl className={styles.meta}>
          <div>
            <dt>Email</dt>
            <dd><a href={`mailto:${lead.homeownerEmail}`}>{lead.homeownerEmail}</a></dd>
          </div>
          {lead.homeownerPhone && (
            <div>
              <dt>Phone</dt>
              <dd><a href={`tel:${lead.homeownerPhone}`}>{lead.homeownerPhone}</a></dd>
            </div>
          )}
          <div>
            <dt>{lead.source === "portal" ? "Added" : "Submitted"}</dt>
            <dd>{formatTimestamp(lead.submissionTimestamp)}</dd>
          </div>
          {lead.source === "portal" && (
            <div>
              <dt>Source</dt>
              <dd>
                Added in the portal{lead.createdBy?.email ? ` by ${lead.createdBy.email}` : ""}
              </dd>
            </div>
          )}
          <div>
            <dt>Lead ID</dt>
            <dd><code>{lead.leadId}</code></dd>
          </div>
        </dl>
      </div>

      <div className={styles.columns}>
        <div className={styles.mainColumn}>
          <section className={`${ui.card} ${styles.viewerCard}`}>
            <div className={ui.cardHeader}>
              <h2 className={ui.cardTitle}>{isEditMode ? "Edit design" : "Design preview"}</h2>
              {!isEditMode && (
                <button
                  ref={editDesignButtonRef}
                  type="button"
                  className={`${ui.buttonSecondary} ${ui.buttonSmall} ${styles.editDesignButton}`}
                  onClick={handleEnterEditMode}
                  disabled={viewingVersionId !== null || restoringVersionId !== null}
                  title={viewingVersionId !== null ? "Return to the current design to edit it" : undefined}
                >
                  Edit design
                </button>
              )}
            </div>

            {viewingVersion && (
              <p className={`${ui.note} ${styles.previewNote}`}>
                Previewing a version from{" "}
                {viewingVersion.createdAt ? formatTimestamp(new Date(viewingVersion.createdAt).getTime()) : "an unknown date"}
                {" "}— not the current design.
              </p>
            )}

            <div className={styles.iframeContainer}>
              {!embedReady && (
                <p className={styles.iframeLoading}>
                  {isEditMode ? "Loading design editor…" : "Loading design viewer…"}
                </p>
              )}
              {isEditMode && embedReady && !designLoadedInEditMode && (
                <p className={styles.iframeLoading}>Applying the current design…</p>
              )}
              <iframe
                ref={iframeRef}
                src={embedUrl}
                sandbox="allow-scripts allow-same-origin"
                /* round12/portal-embed-fullscreen: the embed's own full-screen
                   toggle (round11) renders only when the host grants the
                   permission — without these two the browser reports
                   full-screen as unavailable inside the frame and the button
                   is simply absent. `allow` is the Permissions-Policy form,
                   `allowFullScreen` the legacy attribute older engines read. */
                allow="fullscreen"
                allowFullScreen
                className={styles.iframe}
                title={isEditMode ? "Dock design editor" : "Dock design preview"}
              />
            </div>

            {isEditMode ? (
              <div className={styles.editControls}>
                {saveConflict ? (
                  <div className={styles.conflictBanner}>
                    <p className={styles.conflictMessage}>
                      This design changed since you started editing — your changes here
                      have NOT been saved.
                    </p>
                    <button
                      type="button"
                      className={styles.reloadLatestButton}
                      onClick={handleReloadLatestAfterConflict}
                    >
                      Reload latest design
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      className={styles.labelInput}
                      placeholder="Label this save (optional)"
                      value={saveLabel}
                      onChange={(e) => setSaveLabel(e.target.value)}
                      disabled={isSaving}
                      maxLength={200}
                    />
                    <div className={styles.editButtonRow}>
                      <button
                        ref={cancelEditButtonRef}
                        type="button"
                        className={ui.buttonSecondary}
                        onClick={handleExitEditMode}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={ui.button}
                        onClick={handleSaveEdit}
                        disabled={!canSaveEdit}
                      >
                        {isSaving ? "Saving…" : "Save as new version"}
                      </button>
                    </div>
                    {saveError && (
                      <StatusMessage variant="error" message={saveError} className={ui.error} />
                    )}
                    <p className={styles.viewerHint}>
                      Edit the design above, then save. Unsaved changes are lost if you cancel or leave this page.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <p className={styles.viewerHint}>
                Drag to rotate. Scroll to zoom. Right-click drag to pan.
              </p>
            )}
          </section>

          <div className={styles.pricingRow}>
            <EstimateCard designState={lead.designState} pricing={tenant?.pricing} customBoatBays={tenant?.customBoatBays} />

            {costOutputsEnabled && (
              <QuoteCard quoteResult={lead.quoteResult} homeownerQuote={lead.homeownerQuote} />
            )}
          </div>
        </div>

        <aside className={styles.sideColumn} aria-label="Lead records">
          <ExportsCard lead={lead} tenant={tenant} />

          {/* id targeted by ActivityFeedPanel's version-entry links below (a
              same-page anchor jump, no JS needed) — added here from the
              OUTSIDE, not inside VersionHistoryPanel.tsx itself, so that
              component's own internals stay untouched (scope fence:
              round7/lead-activity-portal links to/references this panel, it
              doesn't refactor it). */}
          <div id="version-history">
            <VersionHistoryPanel
              versions={versions}
              currentVersionId={currentVersionId}
              viewingVersionId={viewingVersionId}
              pendingViewVersionId={pendingViewVersionId}
              onView={handleViewVersion}
              onViewCurrent={handleViewCurrentDesign}
              restoringVersionId={restoringVersionId}
              onRestore={handleRestoreVersion}
              error={versionsError}
              disabled={isEditMode}
            />
          </div>

          <ActivityFeedPanel
            activityFeed={lead.activityFeed}
            noteText={noteText}
            onNoteTextChange={setNoteText}
            onSubmitNote={handleSubmitNote}
            isSubmittingNote={isSubmittingNote}
            noteError={noteError}
            noteAdded={noteAdded}
          />
        </aside>
      </div>
    </main>
  );
}

// submissionStatus → badge tone (text carries the value; colour reinforces).
function statusTone(status: LeadDetail["submissionStatus"]): string {
  switch (status) {
    case "acknowledged": return ui.badgeWarn;
    case "closed": return ui.badgeOk;
    default: return ui.badgeInfo;
  }
}

// Design-based estimate — Round 23.2. The builder ALWAYS sees this range,
// regardless of the tenant's estimate.showToCustomer flag (which only hides the
// range in the customer-facing PDF). Computed from the lead's design, not from
// whatever the PDF showed (the customer's PDF may have omitted it).
function EstimateCard({ designState, pricing, customBoatBays }: {
  designState: unknown;
  pricing?: TenantConfig["pricing"];
  // round9/embed-config — see estimate.ts.
  customBoatBays?: TenantConfig["customBoatBays"];
}) {
  const range = estimateRangeFromDesign(designState, pricing, customBoatBays);
  return (
    <section className={ui.card}>
      <h2 className={ui.cardTitle}>Estimated range</h2>
      <p className={ui.cardHint}>From the design, at your estimate pricing.</p>
      {range ? (
        <>
          <p className={`${ui.statValue} ${styles.figure}`}>
            {formatCurrency(range.low)} – {formatCurrency(range.high)}
          </p>
          <p className={ui.subtle}>
            Preliminary. Always visible to you, even when hidden from the
            homeowner&rsquo;s PDF.
          </p>
        </>
      ) : (
        <p className={ui.subtle}>Not enough design detail to estimate.</p>
      )}
    </section>
  );
}

// Shows the current version's server quote AND, when saved, the pinned
// original homeowner quote (Round 2 f1-version-history) — these can diverge
// once a later version's design/pricing differs from what the homeowner
// originally submitted, so both are labeled and shown, never merged into one
// figure. Gated entirely behind costOutputs at the call site
// (LeadDetailPage) — this component itself doesn't know about entitlements,
// same separation EstimateCard/ExportsCard already keep from tier logic.
function QuoteCard({ quoteResult, homeownerQuote }: {
  quoteResult?: LeadDetail["quoteResult"];
  homeownerQuote?: LeadDetail["homeownerQuote"];
}) {
  return (
    <section className={ui.card}>
      <h2 className={ui.cardTitle}>Quote</h2>
      <p className={ui.cardHint}>
        {homeownerQuote
          ? "What the homeowner was quoted versus what the current design costs."
          : "The server-computed quote for the current design, at your rate card."}
      </p>

      {homeownerQuote && (
        <div className={styles.quoteCompare}>
          <div className={ui.stat}>
            <span className={ui.statLabel}>Quoted to homeowner</span>
            <span className={`${ui.statValue} ${ui.statValueMuted} ${styles.compareFigure}`}>
              {formatCurrency(homeownerQuote.range[0])} – {formatCurrency(homeownerQuote.range[1])}
            </span>
            <span className={ui.subtle}>Pinned at submission.</span>
          </div>
          <div className={ui.stat}>
            <span className={ui.statLabel}>Current quote</span>
            {quoteResult ? (
              <span className={`${ui.statValue} ${styles.compareFigure}`}>
                {formatCurrency(quoteResult.range[0])} – {formatCurrency(quoteResult.range[1])}
              </span>
            ) : (
              <span className={ui.subtle}>No quote was generated for the current version.</span>
            )}
          </div>
        </div>
      )}

      {!quoteResult ? (
        !homeownerQuote && <p className={ui.subtle}>No quote was generated for the current version.</p>
      ) : (
        <CurrentQuoteDetail quoteResult={quoteResult} showRange={!homeownerQuote} />
      )}
    </section>
  );
}

function CurrentQuoteDetail({ quoteResult, showRange }: {
  quoteResult: NonNullable<LeadDetail["quoteResult"]>;
  showRange: boolean;
}) {
  // The detail endpoint returns the lead row verbatim, so quoteResult here
  // is the real, full @dockpro/core QuoteResult — { total, range: [low,
  // high], lineItems: [{..., subtotal}], modifiersApplied, disclaimer } —
  // not a { lowEstimate, highEstimate, lineTotal } shape (see LeadDetail's
  // quoteResult override in lib/api.ts).
  const [low, high] = quoteResult.range;

  return (
    <>
      {showRange && (
        <p className={`${ui.statValue} ${styles.figure}`}>
          {formatCurrency(low)} – {formatCurrency(high)}
        </p>
      )}

      {quoteResult.lineItems.length > 0 && (
        <div className={styles.lineItemsWrap}>
          <table className={`${ui.table} ${styles.lineItemsTable}`}>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Unit price</th>
                <th className={ui.numeric}>Line total</th>
              </tr>
            </thead>
            <tbody>
              {quoteResult.lineItems.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.label}</td>
                  <td className={styles.quiet}>
                    {item.quantity !== undefined
                      ? `${item.quantity}${item.unit ? " " + item.unit : ""}`
                      : "—"}
                  </td>
                  <td className={styles.quiet}>
                    {item.unitPrice !== undefined ? formatCurrency(item.unitPrice) : "—"}
                  </td>
                  <td className={`${ui.numeric} ${styles.lineTotal}`}>{formatCurrency(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {quoteResult.disclaimer && (
        <p className={styles.disclaimer}>{quoteResult.disclaimer}</p>
      )}
    </>
  );
}

// Maps POST /api/portal/leads/{idempotencyKey}/notes's two documented 400
// codes (MISSING_NOTE_TEXT / NOTE_TEXT_TOO_LONG — see DockPro-Backend's
// validateNoteText) to the server's own message VERBATIM, per
// friendlyErrorMessage.ts's own documented convention: "Callers that already
// have a KNOWN-CODE mapping to preserve ... should check err.code themselves
// first and only fall back to this function for the unmapped/unknown case."
// The backend's message is already specific and correct ("Field 'text' is
// required and must be a non-empty string" / "Field 'text' must be at most
// 2000 characters") — inventing new client-side copy here would just
// duplicate it, which is exactly what this function must not do.
//
// Neither is reachable through genuine user interaction — the textarea's
// own maxLength blocks typing/pasting past NOTE_TEXT_TOO_LONG, and the Add
// button disables itself on empty/whitespace-only text, blocking
// MISSING_NOTE_TEXT — but both remain exercisable (and are exercised, see
// LeadDetailPage.activity.test.tsx) by submitting the form directly and
// mocking createLeadNote's rejection, since handleSubmitNote itself has no
// matching client-side short-circuit duplicating this check.
function noteErrorMessage(err: unknown): string {
  if (err instanceof ApiError &&
      (err.code === "MISSING_NOTE_TEXT" || err.code === "NOTE_TEXT_TOO_LONG")) {
    return err.message;
  }
  return friendlyErrorMessage(err, "adding your note");
}

function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleString("en-US", {
    dateStyle: "long", timeStyle: "short",
  });
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n);
}
